const CRYPTO_CURRENCIES = ['USDT', 'USDC', 'BTC', 'ETH'];

/** Stablecoins treated as equivalent for rate calculation */
const STABLECOINS = ['USDT', 'USDC'];

/**
 * Calculate unified display rate as stablecoin/EUR.
 * Works for any transaction shape that has currency + amount pairs.
 * Returns null (render as '–') if the pair is not stablecoin↔EUR.
 */
export function calculateDisplayRate(
  tx: {
    currencyIn?: string | null;
    currencyOut?: string | null;
    amountIn?: string | number | null;
    amountOut?: string | number | null;
  },
): number | null {
  const cIn = tx.currencyIn?.toUpperCase() ?? '';
  const cOut = tx.currencyOut?.toUpperCase() ?? '';
  const aIn = parseFloat(String(tx.amountIn ?? '0'));
  const aOut = parseFloat(String(tx.amountOut ?? '0'));

  if (!aIn || !aOut) return null;

  // Stablecoin IN, EUR OUT  (SELL: client gives crypto)
  if (STABLECOINS.includes(cIn) && cOut === 'EUR') {
    return aIn / aOut; // stablecoin / EUR > 1
  }
  // EUR IN, Stablecoin OUT  (BUY: client gives fiat)
  if (cIn === 'EUR' && STABLECOINS.includes(cOut)) {
    return aOut / aIn; // stablecoin / EUR > 1
  }

  return null;
}

/** Format a display rate to N decimal places (default 4), or '–' if null */
export function formatDisplayRate(rate: number | null, decimals: number = 4): string {
  return rate !== null ? rate.toFixed(decimals) : '–';
}

export function formatAmount(amount: string | number | null | undefined, currency?: string): string {
  if (amount == null) return '-';

  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '-';

  // Crypto gets up to 6 decimals, fiat gets up to 2 (no trailing zeros)
  const maxDecimals = currency && CRYPTO_CURRENCIES.includes(currency) ? 6 : 2;

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(num);
}

export function formatCurrency(amount: string | number | null | undefined, currency: string): string {
  const formatted = formatAmount(amount, currency);
  return formatted === '-' ? '-' : `${formatted} ${currency}`;
}

export function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateLong(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
